import React from "react";

import { ColumnDef } from "@tanstack/react-table";
import dayjs from "dayjs";
import { ArrowUpRight, Music2 } from "lucide-react";

import { CopyButton } from "@/components/common/copy-button";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { makeTooltip } from "@/components/ui/tooltip";
import {
  ARTIST_AUDIT_KIND_HINT,
  artistAuditArtistHref,
  artistAuditEventHref,
  artistAuditFindingMetric,
  artistAuditKindLabel,
  artistAuditSpotifyHref,
} from "@/lib/tina/artist-audit";
import { DeceasedDetails } from "@/pages/tina/audits/artist/components/deceased-details";
import type { ArtistAuditFindingRow, ArtistAuditKind } from "@/types/tina";

export interface UseArtistAuditColumnsOptions {
  onDismiss: (row: ArtistAuditFindingRow) => void;
  dismissingId: string | null;
  /** Deceased cards only. Optional so a caller without it shows Dismiss alone. */
  onApprove?: (row: ArtistAuditFindingRow) => void;
  approvingId?: string | null;
}

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/** `active YYYY-MM → YYYY-MM`, or `active YYYY-MM` when both months are the same. */
const activeWindow = (firstLiveAt: string, lastLiveAt: string | null | undefined): string => {
  const from = dayjs(firstLiveAt).format("YYYY-MM");
  const to = dayjs(lastLiveAt ?? firstLiveAt).format("YYYY-MM");
  return from === to ? `active ${from}` : `active ${from} → ${to}`;
};

/**
 * Columns for the Artist tab of `/tina/audits`.
 *
 * **Nothing here carries a column filter or a sort**, for the reason the venue columns
 * give: the route filters on exactly three toolbar-owned params and has no `sort`, so an
 * inert control is worse than none.
 *
 * **The Finding column is the whole tab, and it is built for one decision: are these two
 * records one act, and if so which survives.** Each artist gets a line — name linked to
 * its IMS page (where the merge is made), followers, gigs, venues, who created it, and a
 * Spotify link so a curator can check which profile is the real act. Followers lead
 * because they are usually the tie-breaker. The metric line under them is the kind's own
 * fact: the shared Spotify id, the venues in common, the broken redirect.
 *
 * **There is no Merge button, by design.** A merge picks a survivor, moves followers,
 * rewrites event lineups and hides a record. That is the artist page's action, not a
 * queue card's. Spec section 9.2. The one artist write here is Approve on a "possibly
 * deceased" card, which only ADDS the `deceased` tag.
 */
export const useArtistAuditColumns = ({
  onDismiss,
  dismissingId,
  onApprove,
  approvingId = null,
}: UseArtistAuditColumnsOptions) =>
  React.useMemo<ColumnDef<ArtistAuditFindingRow>[]>(
    () => [
      {
        id: "severity",
        accessorKey: "severity",
        header: "Severity",
        size: 90,
        cell: ({ row }) =>
          makeTooltip(
            <Badge
              variant={row.original.severity === "high" ? "destructive" : "secondary"}
              className="text-[10px] uppercase"
            >
              {row.original.severity}
            </Badge>,
            row.original.kind === "deceased_artist_with_future_events"
              ? "Sorted high so a dead act billed as performing is seen early — but most are tribute or composer nights: Dismiss those"
              : row.original.severity === "high"
                ? "One Spotify profile on two records, or a multi-word name with both sides booked"
                : "A one-word name or an unbooked side — where honest namesakes live. Still worth a look",
          ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "kind",
        accessorKey: "kind",
        header: "What",
        size: 190,
        cell: ({ row }) => {
          const kind = row.original.kind;
          const hint = ARTIST_AUDIT_KIND_HINT[kind as ArtistAuditKind];
          return makeTooltip(
            <Badge variant="outline" className="text-[10px] font-normal">
              {artistAuditKindLabel(kind)}
            </Badge>,
            // An orchestrator deploy can ship a fifth check before this build knows it;
            // the raw kind plus the fingerprint is still readable.
            hint ?? row.original.fingerprint,
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "finding",
        header: "Finding",
        size: 620,
        cell: ({ row }) => {
          const finding = row.original;
          const artists = finding.evidence.artists ?? [];
          const metric = artistAuditFindingMetric(finding);
          const eventHref = artistAuditEventHref(finding.evidence.event_id);
          const countsUnknown = finding.evidence.event_counts_known === false;
          return (
            <div className="flex flex-col gap-0.5 py-1">
              {artists.length === 0 ? (
                <span className="font-mono text-xs">{finding.fingerprint}</span>
              ) : (
                artists.map((artist) => {
                  const spotifyHref = artistAuditSpotifyHref(artist.spotify_id);
                  return (
                    <div key={artist.artist_id} className="flex items-baseline gap-1.5">
                      <a
                        href={artistAuditArtistHref(artist.artist_id)}
                        className="group inline-flex shrink-0 items-center gap-1 text-xs font-medium hover:underline"
                        onClick={(event) => event.stopPropagation()}
                        title="Open the artist — this is where a merge is made"
                      >
                        {artist.name || artist.artist_id}
                        <ArrowUpRight className="h-3 w-3 shrink-0 text-muted-foreground" />
                      </a>
                      <span className="truncate text-xs text-muted-foreground">
                        {plural(artist.followers, "follower", "followers")}
                        {" · "}
                        {countsUnknown
                          ? "gigs unknown"
                          : `${plural(artist.live_events, "gig", "gigs")} at ${plural(artist.live_venues, "venue", "venues")}`}
                        {" · "}
                        {artist.tina_site ?? artist.created_by ?? "unknown creator"}
                        {artist.created_at ? ` · created ${dayjs(artist.created_at).format("YYYY-MM-DD")}` : ""}
                        {artist.first_live_at ? ` · ${activeWindow(artist.first_live_at, artist.last_live_at)}` : ""}
                      </span>
                      {artist.other_spotify_ids && artist.other_spotify_ids.length > 0 ? (
                        <Badge
                          variant="outline"
                          className="shrink-0 text-[10px] font-normal"
                          title={artist.other_spotify_ids.join(", ")}
                        >
                          absorbed {artist.other_spotify_ids.length}
                        </Badge>
                      ) : null}
                      {spotifyHref ? (
                        <a
                          href={spotifyHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex shrink-0 items-center gap-1 font-mono text-[10px] text-muted-foreground hover:underline"
                          onClick={(event) => event.stopPropagation()}
                          title="Open this Spotify profile"
                        >
                          <Music2 className="h-3 w-3" />
                          {artist.spotify_id}
                        </a>
                      ) : null}
                      {artist.merged_into ? (
                        <span className="shrink-0 text-[10px] uppercase text-muted-foreground">
                          merged
                        </span>
                      ) : null}
                    </div>
                  );
                })
              )}
              <div className="flex items-center gap-2">
                {metric ? (
                  <span
                    className={
                      countsUnknown
                        ? "truncate text-xs font-medium text-amber-700 dark:text-amber-400"
                        : "truncate text-xs text-muted-foreground"
                    }
                  >
                    {metric}
                  </span>
                ) : null}
                {eventHref ? (
                  <a
                    href={eventHref}
                    className="inline-flex shrink-0 items-center gap-1 text-xs hover:underline"
                    onClick={(event) => event.stopPropagation()}
                  >
                    open event
                    <ArrowUpRight className="h-3 w-3" />
                  </a>
                ) : null}
              </div>
              {finding.kind === "deceased_artist_with_future_events" ? (
                <DeceasedDetails evidence={finding.evidence} />
              ) : null}
            </div>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "artist_ids",
        header: "Artist ID",
        size: 190,
        // Every repair path starts with the id — the merge form asks for it, a Firestore
        // lookup asks for it — so it is shown in full with a copy button, never truncated.
        cell: ({ row }) => {
          const artists = row.original.evidence.artists ?? [];
          if (artists.length === 0) return null;
          return (
            <div className="flex flex-col gap-1 py-1">
              {artists.map((artist) => (
                <div key={artist.artist_id} className="flex items-center gap-1">
                  <span className="whitespace-nowrap font-mono text-[10px] text-muted-foreground">
                    {artist.artist_id}
                  </span>
                  <CopyButton
                    text={artist.artist_id}
                    message="Artist ID copied"
                    className="h-6 w-6 shrink-0 p-0"
                  />
                </div>
              ))}
            </div>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "first_seen_at",
        accessorKey: "first_seen_at",
        header: "First seen",
        size: 110,
        cell: ({ row }) =>
          makeTooltip(
            <span className="font-mono text-xs tabular-nums">
              {dayjs(row.original.first_seen_at).format("DD/MM HH:mm")}
            </span>,
            `last seen ${dayjs(row.original.last_seen_at).format("DD/MM/YYYY HH:mm")}`,
          ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "actions",
        header: "",
        size: 120,
        // Dismiss on every card; Approve too on a "possibly deceased" card. Neither is
        // reversible from here. A row that already carries one shows who did it; a row the
        // audit stopped seeing — usually because a curator merged — says so.
        cell: ({ row }) => {
          const finding = row.original;
          // Before `dismissed_at`: an approved row carries both.
          if (finding.approved_at) {
            return makeTooltip(
              <span className="text-xs text-muted-foreground">Approved</span>,
              `Deceased, tagged and blocked ${dayjs(finding.approved_at).format("DD/MM/YYYY HH:mm")} by ${finding.approved_by ?? "unknown"}`,
            );
          }
          if (finding.dismissed_at) {
            return makeTooltip(
              <span className="text-xs text-muted-foreground">Dismissed</span>,
              `${dayjs(finding.dismissed_at).format("DD/MM/YYYY HH:mm")} by ${finding.dismissed_by ?? "unknown"}${
                finding.dismissed_note ? ` — ${finding.dismissed_note}` : ""
              }`,
            );
          }
          if (finding.resolved_at) {
            return makeTooltip(
              <span className="text-xs text-muted-foreground">Fixed</span>,
              `The audit stopped seeing this on ${dayjs(finding.resolved_at).format("DD/MM/YYYY HH:mm")} — usually because the pair was merged`,
            );
          }
          const busy = dismissingId === finding.id || approvingId === finding.id;
          return (
            <div className="flex flex-col items-start gap-1">
              {onApprove && finding.kind === "deceased_artist_with_future_events" ? (
                <Button
                  variant="destructive"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={busy}
                  title="The artist really died: tag them deceased, block their Spotify id, close the card"
                  onClick={(event) => {
                    event.stopPropagation();
                    onApprove(finding);
                  }}
                >
                  Approve
                </Button>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={busy}
                onClick={(event) => {
                  event.stopPropagation();
                  onDismiss(finding);
                }}
              >
                Dismiss
              </Button>
            </div>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    [onDismiss, dismissingId, onApprove, approvingId],
  );
